import { DiscoveryBlock } from '@teko/discovery';
import { ProductCarouselWithHeader } from '@teko/teko-ecommerce';
import { getTrackingData } from '@teko/tracking';
import { ProductCardProps } from '@teko/types/ecommerce';
import { LIMIT_ITEM_PER_PAGE_PRODUCT_LIST } from 'constants/index';
import {
  convertObjectDiscoveryToQueryStringSearch,
  cookDiscoveryFilter,
  isServerless,
} from 'helpers';
import { useSearch } from 'hooks';
import React from 'react';

interface BestSellingProductsBlockProps {
  block: DiscoveryBlock;
  productPerView?: number;
  ssrProducts?: ProductCardProps[];
  isHeadingTitle?: boolean;
}

export const BestSellingProductsBlock: React.FC<
  BestSellingProductsBlockProps
> = ({
  block,
  productPerView,
  ssrProducts = [],
  isHeadingTitle = false,
}: BestSellingProductsBlockProps) => {
  const { result: bestSellingData, isLoading } = useSearch(
    block && {
      ...block.cookedData.paramsObject,
      blockId: block.id,
      pageSize: LIMIT_ITEM_PER_PAGE_PRODUCT_LIST,
    },
    {
      products: ssrProducts,
    },
  );

  if (!isLoading && !bestSellingData?.products?.length) return null;

  // https://jira.teko.vn/browse/UX4BUYER-9152
  // The tracker resolves data-content-name from the first descendant carrying it,
  // which for this block is the first product card. On a slow network the block
  // renders before its cards, so an impression sent then reports contentName
  // 'Unknown'. Track only once the cards exist: the SDK rescans every 750ms,
  // so the impression is deferred rather than lost.
  const hasProducts = !!bestSellingData?.products?.length;

  const link =
    block?.viewMoreUrl ||
    `${convertObjectDiscoveryToQueryStringSearch({
      ...cookDiscoveryFilter(block.cookedData.paramsObject?.filter),
      ...block.cookedData?.paramsObject?.sorting,
    })}`;

  return (
    <ProductCarouselWithHeader
      header={{
        title: block.label,
        titleColor: 'white',
        link: {
          color: 'white',
          href: link,
        },
        isHeadingTitle,
      }}
      productsPerRow={productPerView || 5}
      products={bestSellingData?.products || []}
      background={block.backgroundImageUrl}
      virtual={isServerless()}
      tracking={getTrackingData({
        regionName: `${block.label}HorizontalBlock`,
        isTrackContent: hasProducts,
      })}
    />
  );
};
